/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-console */
/* eslint-disable import/no-default-export */
import { ApplicationController } from '~/app/javascript/controllers/application';

declare global {
  interface Window {
    Turbo: any;
  }
}

class AutocompleteComponentController extends ApplicationController {
  static targets = ["input", "menu"];
  static values = {
    minCharacters: Number,
    url: String
  };

  declare readonly hasInputTarget: boolean;
  declare readonly inputTarget: HTMLInputElement;
  declare readonly hasMenuTarget: boolean;
  declare readonly menuTarget: HTMLElement;
  declare readonly minCharactersValue: number;
  declare readonly urlValue: string;

  searchTimeout: any = null;
  clickOutsideHandler: any = null;

  connect() {
    this.setupEventListeners()
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler)
    }
  }

  setupEventListeners() {
    this.clickOutsideHandler = this.clickOutside.bind(this)
    document.addEventListener('click', this.clickOutsideHandler)

    // Handle focus on input to show initial results for branch dropdown
    if (this.hasInputTarget && this.inputTarget.name === 'branch_name') {
      this.inputTarget.addEventListener('focus', () => {
        this.showInitialResults()
      })
    }

    // Handle focus on input to show initial results for rep dropdown
    if (this.hasInputTarget && this.inputTarget.name === 'sales_rep_search_field') {
      this.inputTarget.addEventListener('focus', () => {
        this.showInitialRepResults()
      })
    }
  }

  async showInitialResults() {
    if (this.inputTarget?.name === 'branch_name' && !this.inputTarget.disabled) {
      const companyInput = document.querySelector('input[name="company_name"]') as HTMLInputElement
      if (companyInput?.value) {
        await this.fetchInitialResults('/view_component/company--branch-dropdown-component', {
          company_name: companyInput.value
        })
      }
    }
  }

  async showInitialRepResults() {
    if (this.inputTarget?.name === 'sales_rep_search_field' && !this.inputTarget.disabled) {
      await this.fetchInitialResults('/view_component/user--rep-dropdown-component')
    }
  }

  search(event: Event) {
    const target = event.target as HTMLInputElement
    if (!target) { return }

    const query = target.value.trim()
    this.performSearch(query)
  }

  performSearch(query: string) {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    if (query.length < this.minCharactersValue) {
      this.hideMenu()
      return
    }

    this.searchTimeout = setTimeout(() => {
      this.submitSearch(query)
    }, 300)
  }

  async submitSearch(query: string) {
    if (!this.urlValue) { return }

    try {
      const formData = new FormData()
      formData.append('q', query)

      // For branch searches, also include the company name
      if (this.inputTarget?.name === 'branch_name') {
        const companyInput = document.querySelector('input[name="company_name"]') as HTMLInputElement
        if (companyInput?.value) {
          formData.append('company_name', companyInput.value)
        }
      }

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      if (csrfToken) {
        formData.append('authenticity_token', csrfToken)
      }

      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })

      if (response.ok) {
        const html = await response.text()
        this.handleTurboStreamResponse(html)
      }
    } catch (error) {
      console.error('Autocomplete search failed:', error)
    }
  }

  handleTurboStreamResponse(html: string) {
    if (window.Turbo) {
      Turbo.renderStreamMessage(html)
      this.showMenu()
    }
  }

  select(event: Event) {
    event.preventDefault()

    const item = event.currentTarget as HTMLElement
    const listItem = item.closest('li') as HTMLElement
    const value = listItem?.dataset.value || ''
    const text = item.textContent?.trim() || ''

    if (this.hasInputTarget) {
      this.inputTarget.value = text
    }

    this.hideMenu()

    // Dispatch selection event
    this.dispatch('selected', {
      detail: { value, text, item }
    })

    // If this is a company selection, trigger cascading update via the company dropdown
    if (this.inputTarget?.name === 'company_name') {
      this.triggerCascadingUpdate(text)
    }

    // If this is a branch selection, trigger rep update via the branch dropdown
    if (this.inputTarget?.name === 'branch_name') {
      this.triggerBranchCascadingUpdate(text)
    }

    // If this is a rep selection, trigger hidden field update
    if (this.inputTarget?.name === 'sales_rep_search_field') {
      this.triggerRepUpdate(text)
    }
  }

  async triggerCascadingUpdate(companyName: string) {
    try {
      const formData = new FormData()
      formData.append('company_name', companyName)
      formData.append('q', '')

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      if (csrfToken) {
        formData.append('authenticity_token', csrfToken)
      }

      const response = await fetch('/view_component/company--dropdown-component', {
        method: 'POST',
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })

      if (response.ok) {
        const html = await response.text()
        if (window.Turbo) {
          Turbo.renderStreamMessage(html)
        }
      }
    } catch (error) {
      console.error('Cascading update failed:', error)
    }
  }

  async triggerBranchCascadingUpdate(branchName: string) {
    try {
      const formData = new FormData()

      // Get the company name from the company dropdown
      const companyInput = document.querySelector('input[name="company_name"]') as HTMLInputElement
      if (companyInput?.value) {
        formData.append('company_name', companyInput.value)
      }

      formData.append('branch_name', branchName)
      formData.append('q', '')

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      if (csrfToken) {
        formData.append('authenticity_token', csrfToken)
      }

      const response = await fetch('/view_component/company--branch-dropdown-component', {
        method: 'POST',
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })

      if (response.ok) {
        const html = await response.text()
        if (window.Turbo) {
          Turbo.renderStreamMessage(html)
        }
      }
    } catch (error) {
      console.error('Branch cascading update failed:', error)
    }
  }

  async triggerRepUpdate(repName: string) {
    try {
      const formData = new FormData()
      formData.append('q', repName)

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      if (csrfToken) {
        formData.append('authenticity_token', csrfToken)
      }

      const response = await fetch('/view_component/user--rep-dropdown-component', {
        method: 'POST',
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })

      if (response.ok) {
        const html = await response.text()
        if (window.Turbo) {
          window.Turbo.renderStreamMessage(html)
        }
      }
    } catch (error) {
      console.error('Rep update failed:', error)
    }
  }

  showMenu() {
    if (this.hasMenuTarget) {
      this.menuTarget.classList.remove('hidden')
      this.menuTarget.style.display = 'block'
    }
  }

  hideMenu() {
    if (this.hasMenuTarget) {
      this.menuTarget.classList.add('hidden')
      this.menuTarget.style.display = 'none'
    }
  }

  clickOutside(event: Event) {
    if (!this.element.contains(event.target as Node)) {
      this.hideMenu()
    }
  }

  navigate(event: KeyboardEvent) {
    event.preventDefault()
    const currentItem = event.currentTarget as HTMLElement
    const currentLi = currentItem.closest('li') as HTMLElement

    const targetLi = event.key === 'ArrowUp'
      ? currentLi?.previousElementSibling as HTMLElement
      : currentLi?.nextElementSibling as HTMLElement

    if (targetLi) {
      const targetDiv = targetLi.querySelector('div[tabindex="0"]') as HTMLElement
      targetDiv?.focus()
    }
  }

  private async fetchInitialResults(url: string, extraParams: Record<string, string> = {}) {
    try {
      const formData = new FormData()
      formData.append('q', '')

      Object.entries(extraParams).forEach(([key, value]) => {
        formData.append(key, value)
      })

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      if (csrfToken) {
        formData.append('authenticity_token', csrfToken)
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })

      if (response.ok) {
        const html = await response.text()
        if (window.Turbo) {
          Turbo.renderStreamMessage(html)
          this.showMenu()
        }
      }
    } catch (error) {
      console.error(`Failed to load initial results from ${url}:`, error)
    }
  }
}

export default AutocompleteComponentController;
